/**
 * RSA-OAEP 4096-bit key pair generation and operations using WebCrypto API.
 * Used for org-level encryption: wrapping/unwrapping per-report DEKs with the org key pair.
 */

const RSA_ALGORITHM: RsaHashedKeyGenParams = {
    name: "RSA-OAEP",
    modulusLength: 4096,
    publicExponent: new Uint8Array([1, 0, 1]),
    hash: "SHA-256",
};

const RSA_IMPORT_PARAMS: RsaHashedImportParams = {
    name: "RSA-OAEP",
    hash: "SHA-256",
};

export async function generateOrgKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(RSA_ALGORITHM, true, ["encrypt", "decrypt"]);
}

export async function exportPublicKeyPem(publicKey: CryptoKey): Promise<string> {
    const exported = await crypto.subtle.exportKey("spki", publicKey);
    const b64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
    const lines = b64.match(/.{1,64}/g)!;
    return `-----BEGIN PUBLIC KEY-----\n${lines.join("\n")}\n-----END PUBLIC KEY-----`;
}

export async function exportPrivateKey(privateKey: CryptoKey): Promise<ArrayBuffer> {
    return crypto.subtle.exportKey("pkcs8", privateKey);
}

export async function importRsaPrivateKey(pkcs8Bytes: ArrayBuffer): Promise<CryptoKey> {
    return crypto.subtle.importKey("pkcs8", pkcs8Bytes, RSA_IMPORT_PARAMS, true, ["decrypt"]);
}

export async function importRsaPublicKey(pem: string): Promise<CryptoKey> {
    const b64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, "")
        .replace(/-----END PUBLIC KEY-----/, "")
        .replace(/\s/g, "");
    const binary = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return crypto.subtle.importKey("spki", binary.buffer, RSA_IMPORT_PARAMS, true, ["encrypt"]);
}

export async function rsaOaepWrapDek(publicKey: CryptoKey, dek: Uint8Array): Promise<ArrayBuffer> {
    return crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, dek);
}

export async function rsaOaepUnwrapDek(privateKey: CryptoKey, wrappedDek: ArrayBuffer): Promise<Uint8Array> {
    const decrypted = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, wrappedDek);
    return new Uint8Array(decrypted);
}
